import { acquisitionDate, fundedDate } from "./pfmPeriod";

/** Événement de calendrier issu d'un compte (hors charges récurrentes). */
export interface AccountCalendarEvent {
  date: Date;
  type: "payout" | "expense" | "funded" | "reset";
  amount: number;
  currency: string;
  label: string;
}

interface AccountLike {
  name: string;
  createdAt?: Date | string | null;
  purchasedAt?: Date | string | null;
  fundedAt?: Date | string | null;
  previousAccountId?: string | null;
  resetAt?: Date | string | null;
  challengeCost?: number | null;
  activationFee?: number | null;
  payouts?: { date: Date | string; amount: number }[];
}

/**
 * Construit les événements de calendrier d'un compte : payouts, frais de
 * challenge, frais d'activation, passage funded, reset du compte.
 *
 * Source unique : cette logique était dupliquée dans PfmView, PropFirmView,
 * DashboardView (deux fois) et useCustomPageData. Chaque copie datait le frais
 * de challenge par `createdAt` — l'horodatage de SAISIE en base — si bien qu'un
 * compte historique acheté en mars apparaissait au jour de sa création dans
 * Edgely. La date d'achat réelle prime désormais, comme pour les KPIs.
 */
export function buildAccountCalendarEvents(
  account: AccountLike,
  currency: string,
): AccountCalendarEvent[] {
  const events: AccountCalendarEvent[] = [];

  for (const p of account.payouts ?? []) {
    events.push({
      date: new Date(p.date),
      type: "payout",
      amount: p.amount,
      currency,
      label: account.name,
    });
  }

  const boughtAt = acquisitionDate(account);
  if (account.challengeCost && boughtAt) {
    events.push({
      date: boughtAt,
      type: "expense",
      amount: account.challengeCost,
      currency,
      label: `${account.name} - Challenge`,
    });
  }

  // fundedDate et non fundedAt seul : un PA créé par la sync puis relié à son
  // challenge n'a pas de fundedAt, son passage est sa date d'arrivée. Avant,
  // ces passages manquaient au calendrier Advanced alors que le Lite les
  // affichait (pfmModel.ts lit déjà fundedDate).
  const passedAt = fundedDate(account);

  if (account.activationFee && passedAt) {
    events.push({
      date: passedAt,
      type: "expense",
      amount: account.activationFee,
      currency,
      label: `${account.name} - Activation`,
    });
  }

  if (passedAt) {
    events.push({
      date: passedAt,
      type: "funded",
      amount: 0,
      currency,
      label: account.name,
    });
  }

  // Reset du compte par la prop firm (challenge cramé puis remis à zéro).
  // Même forme que l'événement "funded" : un jalon sans montant.
  if (account.resetAt) {
    events.push({
      date: new Date(account.resetAt),
      type: "reset",
      amount: 0,
      currency,
      label: account.name,
    });
  }

  return events;
}
